import { supabase } from 'src/lib/supabaseClient';
import { config } from 'src/config/environment';
import { MeetingConfiguration } from 'src/types/meetingConfiguration';

export interface TeamsSessionData {
  subject: string;
  startTime: string;
  endTime: string;
  organizerId?: string; // Optional - will use service account if not provided
  participants?: string[];
  description?: string;
  sessionType?: string; // Session type to determine meeting configuration
  meetingConfig?: Partial<MeetingConfiguration>; // Custom meeting configuration
}

export interface TeamsMeetingResponse {
  meetingId: string;
  joinUrl: string;
  joinUrlBrowser: string;
  joinUrlVisitTeams: string;
  joinUrlDirectClean: string;
  joinUrlSimple: string;
  joinUrlMinimal: string;
  meetingData: any;
  debugInfo: {
    serviceAccountObjectId: string;
    tenantId: string;
    creationTime: string;
    apiResponseKeys: string[];
  };
}

export interface MeetingMetadata {
  provider: 'microsoft_teams';
  meeting_id: string;
  join_url: string;
  conference_id?: string;
  organizer_id: string;
  settings: {
    allowAnonymousUsers: boolean;
    recordAutomatically: boolean;
    lobbyBypassSettings: string;
    allowedPresenters: string;
    allowMeetingChat: boolean;
    createdViaServiceAccount: boolean;
    createdViaTutorAccount: boolean;
  };
}

/**
 * Unified Teams Service - Consolidates all Teams functionality
 * Uses Supabase Edge Functions for server-side operations
 * Supports both service account and tutor account meeting creation
 */
class TeamsServiceUnified {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${config.supabase.url}/functions/v1`;
  }

  /**
   * Get authorization headers for Supabase function calls
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('No active session found');
    }

    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Check if user is authenticated with Teams
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.baseUrl}/teams-meetings`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.isAuthenticated || false;
    } catch (error) {
      console.error('Failed to check Teams authentication:', error);
      return false;
    }
  }

  /**
   * Initiate Teams authentication flow
   */
  async authenticate(returnUrl?: string): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();
      
      const url = new URL(`${this.baseUrl}/teams-auth`);
      url.searchParams.set('action', 'initiate');
      if (returnUrl) {
        url.searchParams.set('returnUrl', returnUrl);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to initiate authentication');
      }

      const data = await response.json();

      if (data.authUrl) {
        console.log('🚀 FRONTEND: About to redirect to Microsoft OAuth')
        console.log('🔗 Auth URL:', data.authUrl)
        console.log('🌐 Current URL before redirect:', window.location.href)

        // Redirect to Microsoft OAuth
        window.location.href = data.authUrl;

        console.log('🔄 FRONTEND: Redirect initiated')
        return true;
      }

      return false;
    } catch (error) {
      console.error('Teams authentication failed:', error);
      return false;
    }
  }

  /**
   * Create a Teams meeting using Supabase Edge Function
   * Supports both service account and tutor account creation
   */
  async createMeeting(sessionData: TeamsSessionData): Promise<TeamsMeetingResponse> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/teams-meetings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'create',
          sessionData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create Teams meeting');
      }

      const data = await response.json();

      return {
        meetingId: data.meetingId,
        joinUrl: data.joinUrl,
        joinUrlBrowser: data.joinUrlBrowser,
        joinUrlVisitTeams: data.joinUrlVisitTeams,
        joinUrlDirectClean: data.joinUrlDirectClean,
        joinUrlSimple: data.joinUrlSimple,
        joinUrlMinimal: data.joinUrlMinimal,
        meetingData: data.meetingData,
        debugInfo: data.debugInfo,
      };
    } catch (error) {
      console.error('Failed to create Teams meeting:', error);
      throw error;
    }
  }

  /**
   * Create a Teams meeting and store it in the database
   * This is the primary method for session meeting creation
   */
  async createAndStoreMeeting(sessionId: string, sessionData: TeamsSessionData): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/teams-meetings`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'create_and_store',
          sessionId,
          sessionData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create and store Teams meeting');
      }

      const data = await response.json();
      return data.success || false;
    } catch (error) {
      console.error('Failed to create and store Teams meeting:', error);
      throw error;
    }
  }

  /**
   * Update an existing Teams meeting
   */
  async updateMeeting(meetingId: string, sessionData: Partial<TeamsSessionData>): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/teams-meetings`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          action: 'update',
          meetingId,
          sessionData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update Teams meeting');
      }

      const data = await response.json();
      return data.success || false;
    } catch (error) {
      console.error('Failed to update Teams meeting:', error);
      throw error;
    }
  }

  /**
   * Cancel/Delete a Teams meeting
   */
  async cancelMeeting(meetingId: string): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/teams-meetings`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({
          action: 'cancel',
          meetingId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel Teams meeting');
      }

      const data = await response.json();
      return data.success || false;
    } catch (error) {
      console.error('Failed to cancel Teams meeting:', error);
      throw error;
    }
  }

  /**
   * Sign out from Teams (remove stored tokens)
   */
  async signOut(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No active user found');
      }

      const { error } = await supabase
        .from('user_integrations')
        .delete()
        .eq('user_id', user.id)
        .eq('provider', 'microsoft_teams');

      if (error) {
        throw error;
      }

      console.log('Successfully signed out from Teams');
    } catch (error) {
      console.error('Failed to sign out from Teams:', error);
      throw error;
    }
  }

  /**
   * Get tutor verification status
   */
  async getTutorVerificationStatus(tutorId: string): Promise<{
    isVerified: boolean;
    azureUserId?: string;
    email?: string;
    requiresReauth?: boolean;
  }> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/teams-auth`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'tutor_status',
          tutorId,
        }),
      });

      if (!response.ok) {
        return { isVerified: false };
      }

      const data = await response.json();
      return data.status || { isVerified: false };
    } catch (error) {
      console.error('Failed to get tutor verification status:', error);
      return { isVerified: false };
    }
  }

  /**
   * Generate verification URL for tutor
   */
  async generateTutorVerificationUrl(tutorId: string): Promise<string | null> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(`${this.baseUrl}/teams-auth`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'generate_verification_url',
          tutorId,
        }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.verificationUrl || null;
    } catch (error) {
      console.error('Failed to generate tutor verification URL:', error);
      return null;
    }
  }

  /**
   * Initialize the service (for compatibility with existing code)
   */
  async initialize(): Promise<boolean> {
    return await this.isAuthenticated();
  }
}

// Export the unified service
export const teamsService = new TeamsServiceUnified();

// For backward compatibility
export const teamsServiceBackend = teamsService;
export default teamsService;
